/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { columnHeaderHeight } from '@atlassian/jira-platform-board-kit/src/common/constants/styles/index.tsx';
import { SwimlaneColumnHeaderContainer } from '@atlassian/jira-platform-board-kit/src/ui/swimlane-column-header/index.tsx';
import SwimlaneWrapper from '@atlassian/jira-platform-board-kit/src/ui/swimlane/wrapper/index.tsx';
import type { IssueLink as DependencyModalIssueLink } from '@atlassian/jira-portfolio-3-dependency-line-detail/src/common/types.tsx';
import { useDependencyLinks } from '@atlassian/jira-portfolio-3-plan-increment-common/src/hooks/use-dependency-links/index.tsx';
import { DependencyLines } from '@atlassian/jira-portfolio-3-plan-increment-common/src/ui/dependency-lines/index.tsx';
import type { IssueLink, IssueId } from '@atlassian/jira-software-board-common/src/index.tsx';
import {
	type MeasurementCacheEntry,
	useVirtual,
} from '@atlassian/jira-software-fast-virtual/src/services/use-virtual/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { Capability } from '../../../../common/capability/index.tsx';
import { issueLinksRemoveRequest } from '../../../../state/actions/issue/issue-link/index.tsx';
import {
	useBoardSelector,
	useBoardActionCreator,
	useBoardStore,
} from '../../../../state/index.tsx';
import { isIncrementPlanningReadOnly } from '../../../../state/selectors/board/board-permissions-selectors.tsx';
import {
	getIssueIdsToShowDependencies,
	getShowOfftrackDependencyLines,
} from '../../../../state/selectors/issue-link/issue-link-selectors.tsx';
import { getIssueLinkTypes } from '../../../../state/selectors/software/software-selectors.tsx';
import { getCollapsedSwimlanes } from '../../../../state/selectors/swimlane/swimlane-selectors.tsx';
import { useHasInteractionStarted } from '../../../../state/state-hooks/use-has-interaction-started/index.tsx';
import {
	columnsWithResolutionSelector,
	platformSwimlaneIdsSelector,
	swimlaneHeightsSelector,
	getIssuesEntriesWithIssueLinksInBoard,
	getIssuesIdsForColumn,
} from '../../../../state/selectors/work/work-selectors.tsx';
import {
	useCapability,
	useIsIncrementPlanningBoard,
	useIsUserBoard,
	useIsJSWBoard,
} from '../../../../state/state-hooks/capabilities/index.tsx';
import ColumnHeader from '../column/header/index.tsx';
import ColumnMenu from '../column/header/menu/index.tsx';
import SwimlaneColumnHeader from '../swimlane-header/index.tsx';
import VirtualizedSwimlane from '../virtualized-swimlane/index.tsx';
import { useAutoScrollSwimlane } from './use-auto-scroll-swimlane/index.tsx';

const SwimlaneColumnHeaderContainerNew = ({
	flexibleColumns,
	isThemed: isSoftwareM1Enabled,
	children,
}: {
	flexibleColumns: boolean;
	isThemed: boolean;
	children: React.ReactNode;
}) => {
	const { isSoftwareFullTheming } = useSoftwareProjectTheming();

	return (
		<div
			css={[
				swimlaneColumnHeaderContainerStyles.base,
				flexibleColumns && swimlaneColumnHeaderContainerStyles.flexible,
				isSoftwareM1Enabled &&
					flexibleColumns &&
					swimlaneColumnHeaderContainerStyles.flexibleThemed,
				isSoftwareFullTheming && swimlaneColumnHeaderContainerStyles.flexibleThemedAddedPadding,
			]}
		>
			{children}
		</div>
	);
};

const SwimlanesContainer = ({
	scrollContainerRef,
}: {
	scrollContainerRef?: React.RefObject<HTMLElement>;
}) => {
	const boardStore = useBoardStore();
	const columns = useBoardSelector(columnsWithResolutionSelector);
	const swimlaneIds = useBoardSelector(platformSwimlaneIdsSelector);
	const collapsedSwimlaneIds = useBoardSelector(getCollapsedSwimlanes);

	const issuesWithLinksById = useBoardSelector(getIssuesEntriesWithIssueLinksInBoard);
	const issueLinkTypes = useBoardSelector(getIssueLinkTypes);
	const showOffTrackDependencies = useBoardSelector(getShowOfftrackDependencyLines);
	const issueIdsToShowDependencies = useBoardSelector(getIssueIdsToShowDependencies);

	const isIpBoard = useIsIncrementPlanningBoard();
	const isUserBoard = useIsUserBoard();
	const isJswBoard = useIsJSWBoard();
	// We can't simply check for isJswBoard because IP Board is also a JSW Board
	const shouldHideColumnHeader = !(isIpBoard || isUserBoard) && isJswBoard;

	const hasDependenciesToShow = issueIdsToShowDependencies.length > 0 || showOffTrackDependencies;

	const [menusShown, setMenusShowns] = useState<Record<number, boolean>>({});
	const isFlexible = useCapability(Capability.FLEXIBLE_BOARD_COLUMNS);
	const setMenuVisibility = useCallback(
		(columnId: number) => (visible: boolean) => {
			setMenusShowns({ [columnId]: visible });
		},
		[],
	);

	const swimlaneHeights = useBoardSelector(swimlaneHeightsSelector);
	const estimatedTotalHeight = useMemo(
		() => Object.values(swimlaneHeights).reduce((acc, height) => acc + height, 0),
		[swimlaneHeights],
	);
	const cacheOptions = useMemo(
		() => ({
			cache: new Map<string, MeasurementCacheEntry>(),
			getCacheKey: (index: number) => swimlaneIds[index],
		}),
		[swimlaneIds],
	);

	const { rows, totalSize, scrollTo, rowStates } = useVirtual({
		rowCount: swimlaneIds.length,
		getDefaultRowSize: (index) => swimlaneHeights[swimlaneIds[index]],
		offsetTop: shouldHideColumnHeader && getIsSoftwareThemingM2Enabled() ? 0 : columnHeaderHeight,
		cacheOptions,
	});

	const hasInteractionStarted =
		useHasInteractionStarted() || !fg('visualisation_with_relative_position_in_board_');

	const [_, { updateSwimlanePositions }] = useDependencyLinks();

	useEffect(() => {
		if (isIpBoard && rowStates) {
			updateSwimlanePositions(rowStates, rows, swimlaneIds, collapsedSwimlaneIds);
		}
	}, [isIpBoard, updateSwimlanePositions, rows, rowStates, swimlaneIds, collapsedSwimlaneIds]);

	useAutoScrollSwimlane(swimlaneIds, scrollTo);

	const renderSwimlanes = () => (
		<>
			{rows.map(({ index, top, forceRemeasure, measure }) => (
				<VirtualizedSwimlane
					scrollContainerRef={scrollContainerRef}
					key={swimlaneIds[index]}
					id={swimlaneIds[index]}
					index={index}
					top={top}
					measure={measure}
					forceRemeasure={forceRemeasure}
					isLastSwimlane={index === swimlaneIds.length - 1}
					isUnscheduledWorkColumnPanel={false}
					shouldBeAbsolutePositioned={hasInteractionStarted}
				/>
			))}
		</>
	);

	const isIPBoardReadOnly = useBoardSelector(isIncrementPlanningReadOnly);

	const onUnlinkIssueAction = useBoardActionCreator(
		(issueLink: IssueLink, issueId: string | number) =>
			issueLinksRemoveRequest({
				issueId,
				issueLink,
			}),
	);

	const onUnlinkIssue = useCallback(
		async (dependencyModalIssueLink: DependencyModalIssueLink) => {
			if (dependencyModalIssueLink) {
				const issueLink: IssueLink = {
					id: dependencyModalIssueLink.itemKey,
					sourceId: dependencyModalIssueLink.sourceItemKey,
					destinationId: dependencyModalIssueLink.targetItemKey,
					linkTypeId: dependencyModalIssueLink.type,
				};

				return onUnlinkIssueAction(issueLink, issueLink.sourceId);
			}
		},
		[onUnlinkIssueAction],
	);

	const getOrderedIssueIdsForColumn = (columnId: number, swimlaneId: string): IssueId[] => {
		return (
			getIssuesIdsForColumn(boardStore.getState(), {
				columnId,
				swimlaneId,
			}) || []
		);
	};

	const SwimlaneColumnHeaderContainerComponent = getIsSoftwareThemingM2Enabled()
		? SwimlaneColumnHeaderContainerNew
		: SwimlaneColumnHeaderContainer;

	return (
		<SwimlanesContainerWrapper isFlexible={isFlexible}>
			<SwimlaneColumnHeaderContainerComponent
				flexibleColumns={isFlexible}
				isThemed={getIsSoftwareThemingEnabled()}
			>
				{columns.map(({ id }, columnIndex) => {
					return (
						<SwimlaneColumnHeaderStable
							id={id}
							key={id}
							isMenuShown={
								fg('fix_board_column_hidden_context_menu')
									? menusShown[id]
									: menusShown[id] || false
							}
							setMenuVisibility={setMenuVisibility}
							columnIndex={columnIndex}
							{...(getIsSoftwareThemingM2Enabled() && {
								hideColumnHeaders: shouldHideColumnHeader,
							})}
						/>
					);
				})}
			</SwimlaneColumnHeaderContainerComponent>
			<SwimlaneWrapper
				flexibleColumns={isFlexible}
				minHeight={
					// eslint-disable-next-line no-nested-ternary
					!hasInteractionStarted && fg('set_static_initial_minheight_for_board')
						? estimatedTotalHeight
						: shouldHideColumnHeader && getIsSoftwareThemingM2Enabled()
							? totalSize
							: totalSize + columnHeaderHeight
				}
				isVirtualised
				{...(shouldHideColumnHeader &&
					getIsSoftwareThemingM2Enabled() && {
						hideTickMask: true,
					})}
			>
				{renderSwimlanes()}
			</SwimlaneWrapper>
			{isIpBoard && hasDependenciesToShow && (
				<DependencyLines
					issuesWithLinksById={issuesWithLinksById}
					showOffTrackDependencies={showOffTrackDependencies}
					issueIdsToShowDependencies={issueIdsToShowDependencies}
					issueLinkTypes={issueLinkTypes}
					onUnlink={onUnlinkIssue}
					canDelete={!isIPBoardReadOnly}
					getOrderedIssueIdsForColumn={getOrderedIssueIdsForColumn}
				/>
			)}
		</SwimlanesContainerWrapper>
	);
};

export const SwimlaneColumnHeaderStable = ({
	id,
	setMenuVisibility,
	isMenuShown,
	columnIndex,
	hideColumnHeaders,
}: {
	id: number;
	setMenuVisibility: (columnId: number) => (visible: boolean) => void;
	isMenuShown: boolean;
	columnIndex: number;
	hideColumnHeaders?: boolean;
}) => {
	const onVisibilityChange = useMemo(() => setMenuVisibility(id), [id, setMenuVisibility]);
	return (
		<SwimlaneColumnHeader
			id={id}
			key={id}
			header={
				hideColumnHeaders && getIsSoftwareThemingM2Enabled() ? null : (
					<ColumnHeader
						id={id}
						isMenuShown={isMenuShown}
						menu={<ColumnMenu columnId={id} onVisibilityChange={onVisibilityChange} />}
					/>
				)
			}
			draggableIndex={columnIndex}
		/>
	);
};

export default SwimlanesContainer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlanesContainerWrapper = styled.div<{ isFlexible?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ isFlexible }) => (isFlexible ? '100%' : 'initial'),
});

const swimlaneColumnHeaderContainerStyles = cssMap({
	base: {
		display: 'flex',
		gridTemplateColumns: 'initial',
		gridAutoFlow: 'column',
		flexFlow: 'row nowrap',
		position: 'sticky',
		top: 0,
		// @ts-expect-error 4 is not one of the expected values for z-index
		zIndex: 4,
		width: 'auto',
	},
	flexible: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
		width: '100%',
	},
	flexibleThemed: {
		gap: token('space.100'),
		paddingTop: 0,
		paddingRight: token('space.050'),
		paddingBottom: 0,
		paddingLeft: token('space.050'),
		boxSizing: 'border-box',
	},
	flexibleThemedAddedPadding: {
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
});
